import {lazy, FC, Suspense} from 'react'
import {Route, Routes, Navigate} from 'react-router-dom'
import {MasterLayout} from '../../_metronic/layout/MasterLayout'
import TopBarProgress from 'react-topbar-progress-indicator'
import {DashboardPageWrapper} from '../pages/dashboard/DashboardPageWrapper'
import {getCSSVariableValue} from '../../_metronic/assets/ts/_utils'
import {WithChildren} from '../../_metronic/helpers'
import {useAuth} from '../modules/auth'
import TasksByUserPage from '../modules/tasks-by-user/TasksByUserPage'
import NotificationsPage from '../modules/notifications/NotificationsPage'
import IntegrationsPage from '../modules/integrations/IntegrationsPage'
import {ConnectDiscordPage} from '../modules/channels/ConnectDiscordPage'
import {MyChannelsPage} from '../modules/channels/MyChannelsPage'
import DevelopersApiPage from '../modules/developers/DevelopersApiPage'
import {ExternalUserGuard} from './ExternalUserGuard'
import {AdminOrManagerAccessGuard} from './AdminOrManagerAccessGuard'
import {NotFoundPage} from './NotFoundPage'

const PrivateRoutes = () => {
  const TeamsPage = lazy(() => import('../modules/team/TeamsPage'))
  const UsersPage = lazy(() => import('../modules/user/UsersPage'))
  const UserPerformancePage = lazy(() => import('../modules/user-performance/UserPerformancePage'))
  const TeamPerformancePage = lazy(() => import('../modules/team-performance/TeamPerformancePage'))
  const ProjectsPage = lazy(() => import('../modules/project/ProjectsPage'))
  const TimeTrackingPage = lazy(() => import('../modules/time-tracking/TimeTrackingPage'))
  const TimeTrackingUserPage = lazy(
    () => import('../modules/time-tracking-user/TimeTrackingUserPage')
  )
  const ClientsPage = lazy(() => import('../modules/clients/ClientsPage'))
  const SprintsPage = lazy(() => import('../modules/sprints/SprintsPage'))
  const ReportsPage = lazy(() => import('../modules/reports/ReportsPage'))
  const ContractsPage = lazy(() => import('../modules/contracts/ContractsPage'))
  const WikiPage = lazy(() => import('../modules/wiki/WikiPage'))
  const CalendarPage = lazy(() => import('../pages/CalendarPage'))
  const TicketsPage = lazy(() => import('../modules/tickets/TicketsPage'))
  const ClientPortalTicketsPage = lazy(
    () => import('../modules/client-portal-tickets/ClientPortalTicketsPage')
  )
  const ConfigsPage = lazy(() => import('../modules/config/ConfigsPage'))
  const SchedulePage = lazy(() => import('../modules/schedule/SchedulePage'))
  const ForecastPage = lazy(() => import('../modules/project-forecast/ForecastPage'))
  const CategoryPage = lazy(() => import('../modules/category/CategoryPage'))
  const TagPage = lazy(() => import('../modules/tags/TagPage'))
  const MyTasksPage = lazy(() => import('../modules/mytasks/MyTasksPage'))
  const InstanceTasksPage = lazy(() => import('../modules/instance-tasks/InstanceTasksPage'))
  const StagePage = lazy(() => import('../modules/stages/StagePage'))
  const HolidaysPage = lazy(() => import('../modules/holidays/HolidaysPage'))
  const BillingsPage = lazy(() => import('../modules/billings/BillingsPage'))
  const ProjectBudgetPage = lazy(() => import('../modules/project-budget/ProjectBudgetPage'))
  const ReferralsPage = lazy(() => import('../modules/referrals/ReferralsPage'))
  const ProjectTemplatesPage = lazy(
    () => import('../modules/project-templates/ProjectTemplatesPage')
  )
  const TaskTemplatesPage = lazy(() => import('../modules/task-templates/TaskTemplatesPage'))
  const ScheduleTemplatesPage = lazy(
    () => import('../modules/schedule-templates/ScheduleTemplatesPage')
  )
  const ClientPortalAccessPage = lazy(
    () => import('../modules/client-portal-access/ClientPortalAccessPage')
  )
  const {currentUser} = useAuth()

  return (
    <Routes>
      {/* O primeiro acesso agora é uma tela de boas-vindas só para quem ainda não fez onboarding. */}
      <Route path='onboarding/*' element={<Navigate to='/dashboard' replace />} />
      <Route element={<MasterLayout />}>
        {/* Redirect to Dashboard or Onboarding after success login/registartion */}
        <Route path='auth/*' element={<Navigate to='/dashboard' />} />
        {/* Pages */}
        <Route path='dashboard' element={<DashboardPageWrapper />} />
        {/* Lazy Modules */}

        <Route
          path='usuarios/:userId/desempenho/*'
          element={
            <SuspensedView>
              <UserPerformancePage />
            </SuspensedView>
          }
        />

        {currentUser?.administrator ? (
          <>
            <Route
              path='teams/*'
              element={
                <SuspensedView>
                  <TeamsPage />
                </SuspensedView>
              }
            />

            <Route
              path='users/*'
              element={
                <SuspensedView>
                  <UsersPage />
                </SuspensedView>
              }
            />

            <Route
              path='time-tracking/*'
              element={
                <SuspensedView>
                  <TimeTrackingPage />
                </SuspensedView>
              }
            />

            <Route
              path='clients/*'
              element={
                <SuspensedView>
                  <ClientsPage />
                </SuspensedView>
              }
            />

            <Route
              path='contracts/*'
              element={
                <SuspensedView>
                  <ContractsPage />
                </SuspensedView>
              }
            />

            <Route
              path='stages/'
              element={
                <SuspensedView>
                  <StagePage />
                </SuspensedView>
              }
            />

            <Route
              path='tasksbyuser/*'
              element={
                <SuspensedView>
                  <TasksByUserPage />
                </SuspensedView>
              }
            />

            <Route
              path='billings/*'
              element={
                <SuspensedView>
                  <BillingsPage />
                </SuspensedView>
              }
            />
            <Route
              path='orcamento-projeto/*'
              element={
                <SuspensedView>
                  <ProjectBudgetPage />
                </SuspensedView>
              }
            />
          </>
        ) : (
          <>
            <Route
              path='teams/*'
              element={
                <AdminOrManagerAccessGuard>
                  <SuspensedView>
                    <TeamsPage />
                  </SuspensedView>
                </AdminOrManagerAccessGuard>
              }
            />

            <Route
              path='users/*'
              element={
                <AdminOrManagerAccessGuard>
                  <SuspensedView>
                    <UsersPage />
                  </SuspensedView>
                </AdminOrManagerAccessGuard>
              }
            />

            <Route
              path='time-tracking/*'
              element={
                <AdminOrManagerAccessGuard>
                  <SuspensedView>
                    <TimeTrackingPage />
                  </SuspensedView>
                </AdminOrManagerAccessGuard>
              }
            />

            <Route
              path='time-tracking-user/'
              element={
                <SuspensedView>
                  <TimeTrackingUserPage />
                </SuspensedView>
              }
            />

            <Route
              path='tasksbyuser/*'
              element={
                <AdminOrManagerAccessGuard>
                  <SuspensedView>
                    <TasksByUserPage />
                  </SuspensedView>
                </AdminOrManagerAccessGuard>
              }
            />

            <Route
              path='clients/*'
              element={
                <AdminOrManagerAccessGuard>
                  <SuspensedView>
                    <ClientsPage />
                  </SuspensedView>
                </AdminOrManagerAccessGuard>
              }
            />

            <Route
              path='contracts/*'
              element={
                <AdminOrManagerAccessGuard>
                  <SuspensedView>
                    <ContractsPage />
                  </SuspensedView>
                </AdminOrManagerAccessGuard>
              }
            />

            <Route
              path='stages/*'
              element={
                <AdminOrManagerAccessGuard>
                  <SuspensedView>
                    <StagePage />
                  </SuspensedView>
                </AdminOrManagerAccessGuard>
              }
            />
          </>
        )}

        <Route
          path='meu-desempenho'
          element={
            currentUser?.id ? (
              <Navigate to={`/usuarios/${currentUser.id}/desempenho`} replace />
            ) : (
              <Navigate to='/404' replace />
            )
          }
        />

        <Route
          path='desempenho-equipe/*'
          element={
            <AdminOrManagerAccessGuard>
              <SuspensedView>
                <TeamPerformancePage />
              </SuspensedView>
            </AdminOrManagerAccessGuard>
          }
        />

        <Route
          path='holidays/*'
          element={
            <AdminOrManagerAccessGuard>
              <SuspensedView>
                <HolidaysPage />
              </SuspensedView>
            </AdminOrManagerAccessGuard>
          }
        />

        <Route
          path='notifications/*'
          element={
            <SuspensedView>
              <NotificationsPage />
            </SuspensedView>
          }
        />

        <Route
          path='category/*'
          element={
            <ExternalUserGuard>
              <SuspensedView>
                <CategoryPage />
              </SuspensedView>
            </ExternalUserGuard>
          }
        />
        <Route
          path='indicacoes/*'
          element={
            <ExternalUserGuard>
              <SuspensedView>
                <ReferralsPage />
              </SuspensedView>
            </ExternalUserGuard>
          }
        />
        <Route
          path='tags/*'
          element={
            <ExternalUserGuard>
              <SuspensedView>
                <TagPage />
              </SuspensedView>
            </ExternalUserGuard>
          }
        />
        <Route
          path='projects/*'
          element={
            <SuspensedView>
              <ProjectsPage />
            </SuspensedView>
          }
        />

        <Route
          path='sprints/*'
          element={
            <ExternalUserGuard>
              <SuspensedView>
                <SprintsPage />
              </SuspensedView>
            </ExternalUserGuard>
          }
        />
        <Route
          path='reports/*'
          element={
            <SuspensedView>
              <ReportsPage />
            </SuspensedView>
          }
        />
        <Route
          path='wiki/*'
          element={
            <ExternalUserGuard>
              <SuspensedView>
                <WikiPage />
              </SuspensedView>
            </ExternalUserGuard>
          }
        />
        <Route
          path='calendar/*'
          element={
            <SuspensedView>
              <CalendarPage />
            </SuspensedView>
          }
        />
        <Route
          path='tickets/*'
          element={
            <SuspensedView>
              <TicketsPage />
            </SuspensedView>
          }
        />
        <Route
          path='client-portal-tickets/*'
          element={
            <SuspensedView>
              <ClientPortalTicketsPage />
            </SuspensedView>
          }
        />
        <Route
          path='configs/*'
          element={
            <AdminOrManagerAccessGuard>
              <SuspensedView>
                <ConfigsPage />
              </SuspensedView>
            </AdminOrManagerAccessGuard>
          }
        />

        {/* Piloto de canais: fora da allowlist do backend as rotas não existem (caem no 404). */}
        {currentUser?.channels_enabled && (
          <>
            <Route
              path='connect/discord'
              element={
                <SuspensedView>
                  <ConnectDiscordPage />
                </SuspensedView>
              }
            />
            <Route
              path='meus-canais'
              element={
                <SuspensedView>
                  <MyChannelsPage />
                </SuspensedView>
              }
            />
          </>
        )}

        <Route
          path='admin/integrations/*'
          element={
            <SuspensedView>
              <IntegrationsPage />
            </SuspensedView>
          }
        />
        <Route
          path='admin/api/*'
          element={
            <SuspensedView>
              <DevelopersApiPage />
            </SuspensedView>
          }
        />
        <Route
          path='admin/client-portal-access/*'
          element={
            currentUser?.administrator || currentUser?.manager ? (
              <SuspensedView>
                <ClientPortalAccessPage />
              </SuspensedView>
            ) : (
              <Navigate to='/error/403' replace />
            )
          }
        />
        <Route
          path='schedules/*'
          element={
            <SuspensedView>
              <SchedulePage />
            </SuspensedView>
          }
        />

        <Route
          path='project-forecast/*'
          element={
            <SuspensedView>
              <ForecastPage />
            </SuspensedView>
          }
        />

        <Route
          path='mytasks/*'
          element={
            <SuspensedView>
              <MyTasksPage />
            </SuspensedView>
          }
        />

        <Route
          path='tarefas/*'
          element={
            <SuspensedView>
              <InstanceTasksPage />
            </SuspensedView>
          }
        />

        <Route
          path='modelos-projetos/*'
          element={
            currentUser?.administrator || currentUser?.manager ? (
              <SuspensedView>
                <ProjectTemplatesPage />
              </SuspensedView>
            ) : (
              <Navigate to='/error/403' replace />
            )
          }
        />

        <Route
          path='modelos-tarefas/*'
          element={
            currentUser?.administrator || currentUser?.manager ? (
              <SuspensedView>
                <TaskTemplatesPage />
              </SuspensedView>
            ) : (
              <Navigate to='/error/403' replace />
            )
          }
        />

        <Route
          path='modelos-cronogramas/*'
          element={
            currentUser?.administrator || currentUser?.manager ? (
              <SuspensedView>
                <ScheduleTemplatesPage />
              </SuspensedView>
            ) : (
              <Navigate to='/error/403' replace />
            )
          }
        />

        <Route path='404' element={<NotFoundPage />} />

        <Route path='*' element={<Navigate to='/404' replace />} />
      </Route>
    </Routes>
  )
}

const SuspensedView: FC<WithChildren> = ({children}) => {
  const baseColor = getCSSVariableValue('--kt-primary')
  TopBarProgress.config({
    barColors: {
      '0': baseColor,
    },
    barThickness: 1,
    shadowBlur: 5,
  })
  return <Suspense fallback={<TopBarProgress />}>{children}</Suspense>
}

export {PrivateRoutes}
